/** @license
 *
 * Copyright 2020 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

//#region Interfaces

import * as common from "@esri/solution-common";
import * as intl from "@arcgis/core/intl";
import * as restTypes from "@esri/arcgis-rest-types";

export type TEnvironments = "dev" | "qa" | "prod";

export interface ICacheBatch<T> {
  cards: T[];
  paging: restTypes.IPagedResponse;
}

export interface ISortableItem {
  createdMs: number;
  numViews: number;
  title: string;
  [key: string]: any;
}

export interface ISpatialReference {
  label: string;
  wkid: number;
  defaultExtent: string;
}

export interface IURLParamPair {
  "k": string;
  "v": string;
}

//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Generates a URL search string from a set of key/value pairs.
 *
 * @param paramPairs Key/value pairs to add to search string
 * @returns URL search string beginning with "?"; empty string if paramPairs is empty
 */
export function addParams(
  paramPairs: IURLParamPair[]
): string {
  let paramString = "";
  let sep = "?";
  paramPairs.forEach((paramPair) => {
    if (paramPair.k && paramPair.v) {
      paramString += sep + paramPair.k + "=" + paramPair.v;
      sep = "&";
    }
  });
  return paramString;
}

/**
 * Indicates if Velocity is available.
 *
 * @param userSession Authentication
 * @returns Promise resolving to true if Velocity is available
 */
export function checkVelocitySupport(
  userSession: common.UserSession | undefined
): Promise<boolean> {
  if (userSession) {
    return common.getVelocityUrlBase(userSession, {}).then(velocityUrl => {
      return Promise.resolve(!!velocityUrl);
    });
  } else {
    return Promise.resolve(false);
  }
}

/**
 * Trims and packages content into the HTML for a single div.
 *
 * @param content A string of HTML to be packaged into a div HTML
 * @returns Packaged content HTML if not null, undefined, or empty; "" otherwise
 */
export function createDivHtml(
  content: string | null | undefined
): string {
  if (content) {
    const htmlString = content.trim();
    if (htmlString) {
      return "<div>" + htmlString + "</div>";
    } else {
      return "";
    }
  } else {
    return "";
  }
}

/**
 * Creates the parameters part of a URL.
 *
 * @param paramPairs A list of objects that each contain the name of the key and the value of the key; each
 * key/value pair is added to the parameters string in order
 * @returns Parameters part of a URL, preceded by "?"
 */
export function createUrlParams(
  paramPairs: IURLParamPair[]
): string {
  let paramString = "";
  let sep = "?";
  paramPairs.forEach((paramPair) => {
    if (paramPair.k && paramPair.v) {
      paramString += sep + paramPair.k + "=" + paramPair.v;
      sep = "&";
    }
  });
  return paramString;
}

/**
 * Constructs a URL to a thumbnail based on various inputs.
 *
 * @param solutionId Id of item that has thumbnail
 * @param thumbnailFileName Filename of thumbnail
 * @param fallBackUrl URL to use if thumbnailFileName is omitted
 * @param portalUrl URL of portal, e.g., "https://www.arcgis.com"
 * @returns The constructed URL
 */
export function generateThumbnailUrl(
  solutionId: string,
  thumbnailFileName: string,
  fallBackUrl: string,
  portalUrl: string,
): string {
  let thumbnailUrl = "";
  if (thumbnailFileName) {
    thumbnailUrl = portalUrl + "/sharing/rest/content/items/" +
      solutionId + "/info/" + thumbnailFileName + createUrlParams([{ k: "w", v: "400" }]);
} else {
    thumbnailUrl = fallBackUrl;
  }
  return thumbnailUrl;
}

/**
 * Gets the portal to use for an anonymous user session.
 *
 * @param authPortalUrl The PortalUrl from the authentication, if available
 * @returns Portal URL to use for the session
 */
export function getAnonymousPortalUrl(
  authPortalUrl?: string
): string {
  const h = authPortalUrl ? authPortalUrl.replace("https://", "").replace("www.", "") : "";

  if (h.endsWith("devext.arcgis.com")) {
    return authPortalUrl ?? "https://www.arcgis.com";
  } else if (h.endsWith("mapsqa.arcgis.com") || h.endsWith("qaext.arcgis.com")) {
    return authPortalUrl ?? "https://www.arcgis.com";
  } else {
    return "https://www.arcgis.com";
  }
}

/**
 * Returns the default extent for the spatial reference matching the supplied wkid in a set of spatial references;
 * in the case of no matches, returns the supplied default extent.
 *
 * @param wkid Spatial reference WKID to look up for its default extent
 * @param srs List of spatial references
 * @param defaultExtent Fallback extent
 * @returns Extent as a string
 */
export function getDefaultExtent(
  wkid: number,
  srs: ISpatialReference[],
  defaultExtent: string
): string {
  srs.some((sr: ISpatialReference) => {
    if (sr.wkid === wkid) {
      defaultExtent = sr.defaultExtent;
      return true;
    }
  });
  return defaultExtent;
}

/**
 * Check the current portal environment
 *
 * @param docHost The hostname of the current page
 * @param authHost The hostname from the authentication, if available
 * @returns Flag indicating the type of telemetry environment
 */
export function getEnvironment(
  docHost: string,
  authHost?: string
): TEnvironments {
  if (authHost) {
    return _getEnvironment(authHost);
  } else {
    return _getEnvironment(docHost);
  }
}

/**
 * Formats a supplied timestamp (or the current time) using a supplied locale.
 *
 * @param timestamp The number of millisecond since midnight January 1, 1970 UTC
 * @param locale The locale to use
 * @return Date string using the "long date" format, e.g., for "fr" (d MMMM y): "20 mai 2020"
 */
export function getLocaleLongDateString(
  timestamp: number,
  locale: string = "en"
): string {
  const converter = new Intl.DateTimeFormat(locale, {
    "year": "numeric",
    "month": "long",
    "day": "numeric"
  });
  const dateString = converter.format(timestamp);

  return dateString;
}

/**
 * Gets a translation.
 *
 * @param translationsObj JSON object containing translation hierarchy
 * @param translationKeywordPath Path into translationsObj for desired string, e.g., if translationsObj is
 * { "buttons": { "retry": "Retry" } }, then one can get the Retry button's text using translationKeywordPath
 * "buttons.retry"
 * @param replacementsObj Object containing replacements for retrieved string. E.g., if translationsObj is
 * { "hello": "Hello, ${name}" } and replacementsObj is { "name": "Solutions" }, the returned string will be
 * "Hello, Solutions"
 * @return translation or "???" if `translationKeywordPath` doesn't correspond to a string in `translationsObj`
 */
export function getTranslation(
  translationsObj: common.IStringValuePair,
  translationKeywordPath: string,
  replacementsObj?: common.IStringValuePair
): string {
  let translation: string = common.getProp(translationsObj, translationKeywordPath);
  if (translation && replacementsObj) {
    translation = intl.substitute(translation, replacementsObj);
  }
  return translation ?? "???";
}

/**
 * Finds a keyword that begins with a specified prefix and returns the text in that keyword that follows the prefix.
 *
 * @param keywords List of keywords to examine
 * @param prefix Required prefix
 * @returns Part of matched keyword following prefix, or "" if keyword prefix is not found; returns first match found
 */
export function getTypeKeywordProp(
  keywords: string[],
  prefix: string
): string {
  let prop = "";
  keywords.some((kw) => {
    if (kw.startsWith(prefix)) {
      prop = kw.substring(prefix.length);
      return true;
    }
    return false;
  });
  return prop;
}

/**
 * Determines if app is in testing mode based on presence of "test" in URL parameters
 *
 * @param search The page's document.location.search
 * @return True if "test" parameter is present
 */
export function inTestingMode(
  search: string
): boolean {
  let testingMode = false;
  if (search.length > 1) {
    // Remove leading "?"
    let paramsString = search.substring(1);

    // Remove trailing hash
    paramsString = paramsString.split("#")[0];

    // Split into params
    const params = paramsString.split("&");

    // Testing?
    testingMode = params.includes("testing=true");
  }
  return testingMode;
}

/**
 * Set translatable portions of global nav's account menu.
 *
 * @param globalNavConfig Global nav configuration structure
 * @param translations Translations for global nav, following same structure as configuration
 * @return Updated globalNavConfig, which is also modified in place
 */
export function mergeTranslationsIntoGlobalNavAccountMenu(
  globalNavConfig: any,
  translations: any
): any {
  globalNavConfig.header.account.label = translations.header.account.label;
  globalNavConfig.header.account.controls = _merge(globalNavConfig.header.account.controls, translations.header.account.controls);
  globalNavConfig.header.account.menus[0] = _merge(globalNavConfig.header.account.menus[0], translations.header.account.menus[0]);
  globalNavConfig.header.account.menus[1] = _merge(globalNavConfig.header.account.menus[1], translations.header.account.menus[1]);
  globalNavConfig.header.account.menus[2] = _merge(globalNavConfig.header.account.menus[2], translations.header.account.menus[2]);
  globalNavConfig.header.account.menus[3] = _merge(globalNavConfig.header.account.menus[3], translations.header.account.menus[3]);
  return globalNavConfig;
}

/**
 * Set translatable portions of global nav's Solutions menu.
 *
 * @param globalNavConfig Global nav configuration structure
 * @param translations Translations for global nav, following same structure as configuration
 * @return Updated globalNavConfig, which is also modified in place
 */
export function mergeTranslationsIntoGlobalNavSolutionsMenu(
  globalNavConfig: any,
  translations: any
): any {
  globalNavConfig.header.menus[0][0] = _merge(globalNavConfig.header.menus[0][0], translations.header.menus[0][0]);
  globalNavConfig.header.menus[0][1] = _merge(globalNavConfig.header.menus[0][1], translations.header.menus[0][1]);
  globalNavConfig.header.menus[1][0] = _merge(globalNavConfig.header.menus[1][0], translations.header.menus[1][0]);
  return globalNavConfig;
}

// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Check the current portal environment
 *
 * @param hostname The hostname to check
 * @returns Flag indicating the type of telemetry environment
 */
function _getEnvironment(
  hostname: string
): TEnvironments {
  const h = hostname.replace("www.", "");

  if (h.endsWith("devext.arcgis.com")) {
    return "dev";
  } else if (h.endsWith("mapsqa.arcgis.com") || h.endsWith("qaext.arcgis.com")) {
    return "qa";
  } else if (h === "arcgis.com" || h.endsWith(".arcgis.com")) {
    return "prod";
  } else {
    return "dev";
  }
}

/**
 * Performs shallow merge of two objects
 *
 * @param a
 * @param b
 */
export function _merge(a: any, b: any): any {
  return {
    ...a,
    ...b
  };
}
